import { useState, useEffect, useCallback } from "react";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, useDroppable, DragOverlay, rectIntersection, pointerWithin } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandItem, CommandList, CommandInput, CommandEmpty, CommandSeparator } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { format, parseISO, addDays, startOfWeek, addWeeks } from "date-fns";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Plus, Factory, Calendar, Package, Clock, TrendingUp, AlertTriangle, User, LogOut, Box, Warehouse, ShoppingCart, BarChart3, CalendarClock, ShoppingBag, Users, KeyRound } from "lucide-react";
import WorkOrderCard from "./components/WorkOrderCard";
import WorkOrderDetailModal from "./components/WorkOrderDetailModal";
import ProductionDashboard from "@/pages/ProductionDashboard";
import PalletLogDashboard from "@/pages/PalletLogDashboard";
import ItemsPage from "@/pages/ItemsPage";
import WorkOrderDetailPage from "@/pages/WorkOrderDetailPage";
import PlanningDashboard from "@/pages/PlanningDashboard";
import PlanningRawMaterialsPage from "@/pages/PlanningRawMaterialsPage";
import PlanningFinishedGoodsPage from "@/pages/PlanningFinishedGoodsPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import SalesOrdersPage from "@/pages/SalesOrdersPage";
import WarehousePage from "@/pages/WarehousePage";
import StorageMapPage from "@/pages/StorageMapPage";
import TrucksPage from "@/pages/TrucksPage";
import TruckDetailPage from "@/pages/TruckDetailPage";
import HoldsPage from "@/pages/HoldsPage";
import TransactionsPage from "@/pages/TransactionsPage";
import ReceiptsPage from "@/pages/ReceiptsPage";
import UsersPage from "@/pages/UsersPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
console.log('BACKEND_URL:', BACKEND_URL);
console.log('WS_URL:', WS_URL);

function App() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  // Native auth: all authenticated users see all routes (no role-based restriction)
  const hasAccess = () => true;
  const isPackTeam = false;
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 0 })); // 0 = Sunday
  const [ws, setWs] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [filterLines, setFilterLines] = useState(["P1", "P2", "P3"]); // Multi-select for lines
  const [selectedCategories, setSelectedCategories] = useState([]); // Multi-select state
  const [viewMode, setViewMode] = useState("week"); // week, month, max, custom
  const [clonedWorkOrder, setClonedWorkOrder] = useState(null);
  const [sideNavCollapsed, setSideNavCollapsed] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [customEndDate, setCustomEndDate] = useState(format(addDays(new Date(), 7), "yyyy-MM-dd"));
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  
  // Submenu expansion state
  const [expandedMenus, setExpandedMenus] = useState({
    operations: true,
    inventory: true,
    warehouse: true
  });
  
  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };
  
  // Create work order form state
  const [newWO, setNewWO] = useState({
    production_line: "P1",
    customer_po: "",
    item_id: "",
    item_name: "",
    subsidiary_id: "2",
    subsidiary_name: "Mockingbird Food Group LLC",
    location_id: "1",
    location_name: "Mockingbird Foods",
    planned_date: format(new Date(), "yyyy-MM-dd"),
    planned_start_time: "06:00",
    planned_end_time: "14:30",
    planned_quantity: "",
    notes: ""
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // AuthContext already attaches Bearer token to axios.defaults.headers
  function AxiosInterceptor() {
    return null;
  }

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Custom collision detection that prioritizes droppable day columns
  const customCollisionDetection = (args) => {
    // First, check for pointer intersections with droppable zones
    const pointerCollisions = pointerWithin(args);
    
    // Filter to only droppable day columns (not individual work orders)
    const droppableCollisions = pointerCollisions.filter(collision => {
      return collision.id.toString().startsWith('droppable-');
    });
    
    // If we found droppable day columns, use those
    if (droppableCollisions.length > 0) {
      return droppableCollisions;
    }
    
    // Otherwise fall back to rectangle intersection
    const rectCollisions = rectIntersection(args);
    return rectCollisions.filter(collision => {
      return collision.id.toString().startsWith('droppable-');
    });
  };

  // WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      console.log("Attempting to connect to WebSocket:", `${WS_URL}/api/ws`);
      const websocket = new WebSocket(`${WS_URL}/api/ws`);

      websocket.onopen = () => {
        console.log("WebSocket connected successfully");
        toast.success("Real-time updates enabled");
      };

      websocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log("WebSocket message received:", message);
          console.log("Message type:", message.type);

          switch (message.type) {
            case "work_order_created":
              console.log("Handling work_order_created, adding to state:", message.data);
              // Only add if this work order doesn't already exist in state
              setWorkOrders(prev => {
                const exists = prev.some(wo => wo.wo_id === message.data.wo_id);
                if (exists) {
                  console.log("Work order already exists, skipping duplicate add");
                  return prev;
                }
                console.log("Previous work orders:", prev.length);
                const updated = [...prev, message.data];
                console.log("Updated work orders:", updated.length);
                return updated;
              });
              toast.success("New work order created");
              break;
            case "work_order_updated":
              console.log("Handling work_order_updated:", message.data.wo_id);
              setWorkOrders(prev => prev.map(wo =>
                wo.wo_id === message.data.wo_id ? message.data : wo
              ));
              toast.info("Work order updated");
              break;
            case "work_order_rescheduled":
              console.log("Handling work_order_rescheduled:", message.data.wo_id);
              setWorkOrders(prev => prev.map(wo =>
                wo.wo_id === message.data.wo_id ? message.data : wo
              ));
              toast.info("Work order rescheduled");
              break;
            case "work_order_deleted":
              console.log("Handling work_order_deleted:", message.data.wo_id);
              setWorkOrders(prev => prev.filter(wo => wo.wo_id !== message.data.wo_id));
              toast.info("Work order deleted");
              break;
            case "items_sync_started":
              toast.info("Syncing latest items from NetSuite...");
              break;
            case "items_updated":
              // Refresh items data when sync completes
              if (typeof fetchItems === 'function') {
                fetchItems();
              }
              toast.success("Items synced successfully from NetSuite");
              break;
            case "boms_sync_started":
              toast.info("Syncing latest BOMs from NetSuite...");
              break;
            case "boms_updated":
              // Fix: Re-fetch items so the "has_bom" flag updates in the dropdown immediately
              if (typeof fetchItems === 'function') {
                fetchItems();
              }
              toast.success("BOMs synced successfully from NetSuite");
              break;
            case "inventory_sync_started":
              toast.info("Inventory sync started...");
              break;
            case "inventory_updated":
              // Update inventory levels in items state
              setItems(prevItems => {
                const updatedItems = [...prevItems];
                message.data.forEach(updatedItem => {
                  const index = updatedItems.findIndex(item => item.sku === updatedItem.sku);
                  if (index !== -1) {
                    updatedItems[index] = { ...updatedItems[index], on_hand: updatedItem.on_hand };
                  }
                });
                return updatedItems;
              });
              toast.success("Inventory levels have been updated.");
              break;
            case "sales_orders_sync_started":
              toast.info("Sales orders sync started...");
              break;
            case "sales_orders_updated":
              // Sales orders have been updated, dispatch event for planning page to refetch data
              window.dispatchEvent(new CustomEvent('websocket-message', {
                detail: { type: 'sales_orders_updated' }
              }));
              break;
            case "work_orders_updated":
              // Work orders have been synced from NetSuite, upsert into state
              if (message.data && Array.isArray(message.data)) {
                setWorkOrders(prev => {
                  const updated = [...prev];
                  message.data.forEach(newWO => {
                    const existingIndex = updated.findIndex(wo => wo.wo_id === newWO.wo_id);
                    if (existingIndex !== -1) {
                      // Update existing work order
                      updated[existingIndex] = newWO;
                    } else {
                      // Add new work order
                      updated.push(newWO);
                    }
                  });
                  return updated;
                });
              }
              toast.success("Work orders have been synced from NetSuite.");
              break;
            default:
              break;
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };
      
      websocket.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
      
      websocket.onclose = () => {
        console.log("WebSocket disconnected, reconnecting...");
        setTimeout(connectWebSocket, 3000);
      };
      
      setWs(websocket);
    };
    
    connectWebSocket();
    
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  // Fetch initial data when user is authenticated (so token is sent). Re-run when token/user changes (e.g. after login).
  useEffect(() => {
    if (!user) return;
    fetchWorkOrders();
    fetchItems();
    fetchSubsidiaries();
    fetchLocations();
  }, [user]);

  // Note: BOM sync is now triggered manually from the planning pages
  // to avoid performance issues and duplicate calls

  // Persist and restore current page
  useEffect(() => {
    // Restore from localStorage on mount
    const savedPath = localStorage.getItem('rudder_current_path');
    if (savedPath && location.pathname === '/') {
      navigate(savedPath, { replace: true });
    }
  }, []);

  useEffect(() => {
    // Save to localStorage whenever location changes
    if (location.pathname !== '/') {
      localStorage.setItem('rudder_current_path', location.pathname);
    }
  }, [location.pathname]);

  const fetchWorkOrders = async () => {
    try {
      const response = await axios.get(`${API}/work-orders`);
      setWorkOrders(response.data);
    } catch (error) {
      console.error("Error fetching work orders:", error);
      toast.error("Failed to load work orders");
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/items`);
      setItems(response.data);
      
      // Immediately ensure assembly flags are fresh by calling reconciliation
      try {
        await axios.post(`${API}/maintenance/reconcile-assembly-flags`);
        console.log("Assembly flags reconciled immediately after fetching items");
      } catch (error) {
        console.warn("Failed to reconcile assembly flags, but continuing anyway:", error);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
      toast.error("Failed to load items");
    }
  };

  const fetchSubsidiaries = async () => {
    try {
      const response = await axios.get(`${API}/subsidiaries`);
      setSubsidiaries(response.data);
    } catch (error) {
      console.error("Error fetching subsidiaries:", error);
      toast.error("Failed to load subsidiaries");
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Failed to load locations");
    }
  };

  const handleCreateWorkOrder = async () => {
    if (!newWO.item_id || !newWO.planned_quantity || !newWO.subsidiary_id || !newWO.location_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate that planned_date is not in the past
    const [year, month, day] = newWO.planned_date.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Allow current and future dates only
    if (selectedDate < today) {
      toast.error("Work orders can only be created for today or future dates");
      return;
    }

    try {
      // Robust item finding: check both item_id and id properties
      const selectedItem = items.find(item => item.item_id === newWO.item_id || item.id === newWO.item_id);
      
      const payload = {
        ...newWO,
        // Ensure valid item name: check itemid, displayname, then name
        item_name: selectedItem?.itemid || selectedItem?.displayname || selectedItem?.name || "Unknown Item",
        planned_quantity: parseFloat(newWO.planned_quantity)
      };

      await axios.post(`${API}/work-orders`, payload);
      toast.success("Work order created successfully");
      setShowCreateModal(false);
      
      // Reset form with defaults
      setNewWO({
        production_line: "P1",
        customer_po: "",
        item_id: "",
        item_name: "",
        subsidiary_id: "2",
        subsidiary_name: "Mockingbird Food Group LLC",
        location_id: "1",
        location_name: "Mockingbird Foods",
        planned_date: format(new Date(), "yyyy-MM-dd"),
        planned_start_time: "06:00",
        planned_end_time: "14:30",
        planned_quantity: "",
        notes: ""
      });
    } catch (error) {
      console.error("Error creating work order:", error);
      // Show the specific error message from the server
      let errorMsg = error.response?.data?.detail || error.message;
      // Handle object/array error messages (e.g. validation errors) to ensure they are readable
      if (typeof errorMsg === 'object') {
        errorMsg = JSON.stringify(errorMsg);
      }
      toast.error(`Failed to create work order: ${errorMsg}`);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    
    if (!over) return;
    
    // Extract date from droppable ID (format: "droppable-YYYY-MM-DD")
    const overId = over.id.toString();
    const parts = overId.split("-");
    
    if (parts[0] !== "droppable") return;
    
    const newDate = `${parts[1]}-${parts[2]}-${parts[3]}`;
    const workOrder = workOrders.find(wo => wo.wo_id === active.id);
    
    if (!workOrder || workOrder.planned_date === newDate) return;
    
    // Optimistically update the UI immediately
    setWorkOrders(prev => prev.map(wo => 
      wo.wo_id === active.id 
        ? { ...wo, planned_date: newDate }
        : wo
    ));
    
    try {
      await axios.put(`${API}/work-orders/${active.id}/reschedule`, {
        new_date: newDate
      });
      toast.success("Work order rescheduled");
      // WebSocket will sync with other clients
    } catch (error) {
      console.error("Error rescheduling work order:", error);
      toast.error("Failed to reschedule work order");
      // Revert on error
      fetchWorkOrders();
    }
  };

  const handleWorkOrderClick = (workOrder) => {
    // For in_progress or completed work orders, navigate to detail page
    if (workOrder.status === 'in_progress' || workOrder.status === 'completed') {
      navigate(`/work-order/${workOrder.wo_id}?from=schedule`);
    } else {
      // For scheduled work orders, open the modal
      setSelectedWorkOrder(workOrder);
      setShowDetailModal(true);
    }
  };

  const handleUpdateWorkOrder = async (updatedData) => {
    if (!selectedWorkOrder) return;
    
    try {
      await axios.put(`${API}/work-orders/${selectedWorkOrder.wo_id}`, updatedData);
      toast.success("Work order updated successfully");
      // WebSocket will handle the state update
    } catch (error) {
      console.error("Error updating work order:", error);
      toast.error("Failed to update work order");
    }
  };

  const handleDeleteWorkOrder = async (woId) => {
    try {
      await axios.delete(`${API}/work-orders/${woId}`);
      toast.success("Work order deleted successfully");
      setShowDetailModal(false);
      // WebSocket will handle the state update
    } catch (error) {
      console.error("Error deleting work order:", error);
      toast.error("Failed to delete work order");
    }
  };

  const handleCloneWorkOrder = (workOrder) => {
    // Create a clean copy with all necessary fields
    const cloneData = {
      production_line: workOrder.production_line,
      item_id: workOrder.item_id,
      item_name: workOrder.item_name,
      subsidiary_id: workOrder.subsidiary_id,
      subsidiary_name: workOrder.subsidiary_name,
      location_id: workOrder.location_id,
      location_name: workOrder.location_name,
      planned_start_time: workOrder.planned_start_time,
      planned_end_time: workOrder.planned_end_time,
      planned_quantity: workOrder.planned_quantity,
      notes: workOrder.notes || ""
    };
    
    console.log("Cloning work order:", cloneData);
    setClonedWorkOrder(cloneData);
    toast.success("Work order copied. Click on a day to paste.");
    setShowDetailModal(false);
  };

  // Generate days based on view mode
  const getDaysForView = () => {
    switch (viewMode) {
      case "week":
        return Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));
      case "month":
        // Show 4 weeks (28 days) starting from currentWeek
        return Array.from({ length: 28 }, (_, i) => addDays(currentWeek, i));
      case "max":
        // Current week minus 2 weeks, plus 13 weeks = 16 weeks total (112 days)
        const startDate = addWeeks(currentWeek, -2);
        return Array.from({ length: 112 }, (_, i) => addDays(startDate, i));
      case "custom":
        // Custom date range
        const start = parseISO(customStartDate);
        const end = parseISO(customEndDate);
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1; // +1 to include end date
        return Array.from({ length: days }, (_, i) => addDays(start, i));
      default:
        return Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));
    }
  };
  
  const weekDays = getDaysForView();

  // Toggle production line filter
  const toggleLineFilter = (line) => {
    setFilterLines(prev => {
      if (prev.includes(line)) {
        return prev.filter(l => l !== line);
      } else {
        return [...prev, line];
      }
    });
  };

  
  // Helper to derive category (matches Planning page logic)
  const getCategory = (item) => item?.product_group || item?.costcategoryname || "Uncategorized";

  // Get unique categories from items
  const availableCategories = [...new Set(items.map(getCategory))].sort();

  // Apply filters to work orders
  const filteredWorkOrders = workOrders.filter(wo => {
    const lineMatch = filterLines.includes(wo.production_line);
    
    // Find the item definition for this work order
    const item = items.find(i => i.item_id === wo.item_id || i.id === wo.item_id);
    const category = getCategory(item);
    
    // Check if category is selected (if none selected, show all)
    const categoryMatch = selectedCategories.length === 0 || selectedCategories.includes(category);
    
    return lineMatch && categoryMatch;
  });

  // Group work orders by date, sorted by production line then start time
  const getWorkOrdersForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return filteredWorkOrders
      .filter(wo => wo.planned_date === dateStr)
      .sort((a, b) => {
        // First sort by production line (P1, P2, P3)
        const lineCompare = a.production_line.localeCompare(b.production_line);
        if (lineCompare !== 0) return lineCompare;
        
        // Then sort by start time within the same line
        if (a.planned_start_time && b.planned_start_time) {
          return a.planned_start_time.localeCompare(b.planned_start_time);
        }
        return 0;
      });
  };

  // Droppable zone component (single calendar for all lines)
  const DroppableDay = ({ date, children }) => {
    const droppableId = `droppable-${format(date, "yyyy-MM-dd")}`;
    const { setNodeRef, isOver } = useDroppable({
      id: droppableId,
      data: {
        type: 'day-column',
        date: format(date, "yyyy-MM-dd")
      }
    });

    // Check if this is today
    const today = format(new Date(), "yyyy-MM-dd");
    const isToday = format(date, "yyyy-MM-dd") === today;

    const handleDayClick = async (e) => {
    // Only open modal if clicking on empty space (not on work order cards)
    if (e.target.closest('[data-testid^="work-order-card-"]')) {
      return;
    }
    
    // If there's a cloned work order, paste it
    if (clonedWorkOrder) {
      try {
        const pasteDate = format(date, "yyyy-MM-dd");
        
        // Validate paste date is not in the past
        const [pYear, pMonth, pDay] = pasteDate.split('-').map(Number);
        const pasteDateTime = new Date(pYear, pMonth - 1, pDay);
        pasteDateTime.setHours(0, 0, 0, 0);

        const todayCheck = new Date();
        todayCheck.setHours(0, 0, 0, 0);

        if (pasteDateTime < todayCheck) {
          toast.error("Cannot paste work order to a past date");
          setClonedWorkOrder(null);
          return;
        }
        
        // Ensure we have a valid item by looking it up
        const originalItem = items.find(i => 
          i.id === clonedWorkOrder.item_id || 
          i.item_id === clonedWorkOrder.item_id
        );
        
        if (!originalItem) {
          toast.error("Cannot clone: Original item not found");
          setClonedWorkOrder(null);
          return;
        }
        
        // Get the best item name
        const effectiveItemName = originalItem.itemid || 
                                originalItem.displayname || 
                                originalItem.name || 
                                clonedWorkOrder.item_name || 
                                "Unknown Item";

        // Build payload with all required fields
        const payload = {
          production_line: clonedWorkOrder.production_line || "P1",
          item_id: clonedWorkOrder.item_id,
          item_name: effectiveItemName,
          // Subsidiary and Location - use original or defaults
          subsidiary_id: clonedWorkOrder.subsidiary_id || "2",
          subsidiary_name: clonedWorkOrder.subsidiary_name || "Mockingbird Food Group LLC",
          location_id: clonedWorkOrder.location_id || "1",
          location_name: clonedWorkOrder.location_name || "Mockingbird Foods",
          // Date and time
          planned_date: pasteDate,
          planned_start_time: clonedWorkOrder.planned_start_time || "06:00",
          planned_end_time: clonedWorkOrder.planned_end_time || "14:30",
          // Quantity - ensure it's a valid number
          planned_quantity: parseFloat(clonedWorkOrder.planned_quantity) || 0,
          notes: clonedWorkOrder.notes || ""
        };
        
        // Validate quantity
        if (payload.planned_quantity <= 0) {
          toast.error("Cannot clone: Invalid quantity");
          setClonedWorkOrder(null);
          return;
        }

        console.log("Cloning work order with payload:", payload);
        
        await axios.post(`${API}/work-orders`, payload);
        toast.success(`Work order pasted to ${format(date, "MMM d")}`);
        setClonedWorkOrder(null);
      } catch (error) {
        console.error("Error pasting work order:", error);
        // Show the specific error message from the server
        let errorMsg = error.response?.data?.detail || error.message;
        // Handle object/array error messages (e.g. validation errors) to ensure they are readable
        if (typeof errorMsg === 'object') {
          errorMsg = JSON.stringify(errorMsg);
        }
        toast.error(`Failed to paste work order: ${errorMsg}`);
      }
      return;
    }
    
    // Open create modal and pre-fill the date
    setNewWO(prev => ({
      ...prev,
      planned_date: format(date, "yyyy-MM-dd")
    }));
    setShowCreateModal(true);
  };

    return (
      <div 
        ref={setNodeRef}
        className={`day-column ${isOver ? 'day-column-over' : ''} ${isToday ? 'day-column-today' : ''}`}
        data-testid={`day-column-${format(date, "yyyy-MM-dd")}`}
        style={{ position: 'relative' }}
        onClick={handleDayClick}
      >
        {children}
      </div>
    );
  };

  // Statistics (based on filtered work orders)
  const stats = {
    total: filteredWorkOrders.length,
    scheduled: filteredWorkOrders.filter(wo => wo.status === "scheduled").length,
    inProgress: filteredWorkOrders.filter(wo => wo.status === "in_progress").length,
    completed: filteredWorkOrders.filter(wo => wo.status === "completed").length
  };

  // Get unique categories from items
  const categories = [...new Set(items.map(item => item.category))].sort();

  return (
    <>
      <AxiosInterceptor />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/*" element={
          <ProtectedRoute>
        <div className="App">
          <Toaster position="top-right" />
      
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <button 
            className="menu-toggle" 
            onClick={() => setSideNavCollapsed(!sideNavCollapsed)}
            data-testid="menu-toggle"
            title={sideNavCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sideNavCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          <div className="header-left">
            <img 
              src="https://customer-assets.emergentagent.com/job_mfg-workorder-app/artifacts/fp1li17d_Mockingbird-Food-Group_Black.png" 
              alt="Mockingbird Food Group" 
              className="header-logo"
              style={{ backgroundColor: 'transparent' }}
            />
            <h1 className="header-title">Rudder</h1>
          </div>
          <div className="header-right">
            <Popover>
              <PopoverTrigger asChild>
                <button className="header-btn" data-testid="user-btn">
                  <User className="w-4 h-4" />
                  <span>{user?.display_name || user?.email || "User"}</span>
                  <ChevronDown className="w-3 h-3 ml-1" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1" align="end">
                {user?.role === "admin" && (
                  <button
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                    onClick={() => { navigate("/users"); document.body.click?.(); }}
                  >
                    <Users className="w-4 h-4" />
                    Users
                  </button>
                )}
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                  onClick={() => { setShowChangePasswordModal(true); document.body.click?.(); }}
                >
                  <KeyRound className="w-4 h-4" />
                  Change password
                </button>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded text-red-600"
                  onClick={() => logout()}
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </PopoverContent>
            </Popover>
            <ChangePasswordModal
              open={showChangePasswordModal}
              onOpenChange={setShowChangePasswordModal}
              userId={user?.user_id}
            />
          </div>
        </div>
      </header>

      <div className="app-layout">
        {/* Side Navigation */}
        <aside className={`side-nav ${sideNavCollapsed ? 'side-nav-collapsed' : ''}`}>
          <nav className="nav-menu">
            
            {/* SPECIAL MENU FOR PACK TEAM */}
            {isPackTeam && (
              <button 
                className={`nav-item ${location.pathname === "/operations/packing" ? "nav-item-active" : ""}`}
                onClick={() => navigate("/operations/packing")}
                title="Pallet Log"
              >
                <Package className="w-4 h-4" />
                {!sideNavCollapsed && <span>Pallet Log</span>}
              </button>
            )}

            {/* STANDARD MENU (Hidden for Pack Team) */}
            {!isPackTeam && (
              <>
                {/* Operations Parent Menu */}
                {(hasAccess("/schedule") || hasAccess("/dashboard")) && (
                <div className="nav-section">
                  <button 
                    className="nav-item nav-parent"
                    onClick={() => toggleMenu('operations')}
                    title="Operations"
                  >
                    <Factory className="w-4 h-4" />
                    {!sideNavCollapsed && (
                      <>
                        <span>Operations</span>
                        {expandedMenus.operations ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                      </>
                    )}
                  </button>
                  {!sideNavCollapsed && expandedMenus.operations && (
                    <div className="nav-submenu">
                      {hasAccess("/schedule") && (
                        <button 
                          className={`nav-item nav-subitem ${location.pathname === "/schedule" || location.pathname === "/" ? "nav-item-active" : ""}`}
                          onClick={() => navigate("/schedule")}
                          title="Scheduling"
                        >
                          <Calendar className="w-4 h-4" />
                          <span>Scheduling</span>
                        </button>
                      )}
                      {hasAccess("/dashboard") && (
                        <button 
                          className={`nav-item nav-subitem ${location.pathname === "/dashboard" ? "nav-item-active" : ""}`}
                          onClick={() => navigate("/dashboard")}
                          title="Dashboard"
                        >
                          <BarChart3 className="w-4 h-4" />
                          <span>Dashboard</span>
                        </button>
                      )}
                      {/* ADD PACKING LINK FOR ADMINS TOO */}
                      <button 
                        className={`nav-item nav-subitem ${location.pathname === "/operations/packing" ? "nav-item-active" : ""}`}
                        onClick={() => navigate("/operations/packing")}
                        title="Pallet Log"
                      >
                        <Package className="w-4 h-4" />
                        <span>Packing</span>
                      </button>
                    </div>
                  )}
                </div>
                )}

                {/* Inventory Parent Menu */}
                {(hasAccess("/items") || hasAccess("/planning") || hasAccess("/purchase-orders") || hasAccess("/sales-orders")) && (
                <div className="nav-section">
                  <button 
                    className="nav-item nav-parent"
                    onClick={() => toggleMenu('inventory')}
                    title="Inventory"
                  >
                    <Box className="w-4 h-4" />
                    {!sideNavCollapsed && (
                      <>
                        <span>Inventory</span>
                        {expandedMenus.inventory ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                      </>
                    )}
                  </button>
                  {!sideNavCollapsed && expandedMenus.inventory && (
                    <div className="nav-submenu">
                      {hasAccess("/items") && (
                        <button 
                          className={`nav-item nav-subitem ${location.pathname === "/items" ? "nav-item-active" : ""}`}
                          onClick={() => navigate("/items")}
                          title="Items"
                        >
                          <Package className="w-4 h-4" />
                          <span>Items</span>
                        </button>
                      )}
                      {hasAccess("/planning") && (
                        <>
                          <button 
                            className={`nav-item nav-subitem ${location.pathname === "/planning" ? "nav-item-active" : ""}`}
                            onClick={() => navigate("/planning")}
                            title="Planning Dashboard"
                          >
                            <CalendarClock className="w-4 h-4" />
                            <span>Planning</span>
                          </button>
                          <button 
                            className={`nav-item nav-subitem-nested ${location.pathname === "/planning/raw-materials" ? "nav-item-active" : ""}`}
                            onClick={() => navigate("/planning/raw-materials")}
                            title="Raw Materials"
                          >
                            <span>Raw Materials</span>
                          </button>
                          <button 
                            className={`nav-item nav-subitem-nested ${location.pathname === "/planning/finished-goods" ? "nav-item-active" : ""}`}
                            onClick={() => navigate("/planning/finished-goods")}
                            title="Finished Goods"
                          >
                            <span>Finished Goods</span>
                          </button>
                        </>
                      )}
                      {hasAccess("/purchase-orders") && (
                        <button 
                          className={`nav-item nav-subitem ${location.pathname === "/purchase-orders" ? "nav-item-active" : ""}`}
                          onClick={() => navigate("/purchase-orders")}
                          title="Purchase Orders"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          <span>Purchase Orders</span>
                        </button>
                      )}
                      {hasAccess("/sales-orders") && (
                        <button 
                          className={`nav-item nav-subitem ${location.pathname === "/sales-orders" ? "nav-item-active" : ""}`}
                          onClick={() => navigate("/sales-orders")}
                          title="Sales Orders"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          <span>Sales Orders</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
                )}
              </>
            )}

            {/* Warehouse Parent Menu - COMMENTED OUT FOR NOW */}
            {/* <div className="nav-section">
              <button 
                className={`nav-item nav-parent ${location.pathname.startsWith("/warehouse") ? "nav-item-active" : ""}`}
                onClick={() => toggleMenu('warehouse')}
                title="Warehouse"
              >
                <Warehouse className="w-4 h-4" />
                {!sideNavCollapsed && (
                  <>
                    <span>Warehouse</span>
                    {expandedMenus.warehouse ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                  </>
                )}
              </button>

              {!sideNavCollapsed && expandedMenus.warehouse && (
                <div className="nav-submenu">
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/warehouse" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/warehouse")}
                    title="Dashboard"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Dashboard</span>
                  </button>
                  <button 
                    className={`nav-item nav-child ${location.pathname === "/warehouse/storage-map" ? "nav-item-active" : ""}`}
                    onClick={() => navigate("/warehouse/storage-map")}
                    title="Storage Map"
                  >
                    <Box className="w-4 h-4" />
                    <span>Storage Map</span>
                  </button>
                </div>
              )}
            </div> */}

            {/* Reporting - COMMENTED OUT FOR NOW */}
            {/* <button 
              className={`nav-item ${location.pathname === "/reporting" ? "nav-item-active" : ""}`}
              onClick={() => navigate("/reporting")}
              data-testid="nav-reporting"
              title="Reporting"
            >
              <TrendingUp className="w-4 h-4" />
              {!sideNavCollapsed && <span>Reporting</span>}
            </button> */}
            
            {/* Settings - COMMENTED OUT FOR NOW */}
            {/* <button 
              className={`nav-item ${location.pathname === "/settings" ? "nav-item-active" : ""}`}
              onClick={() => navigate("/settings")}
              data-testid="nav-settings"
              title="Settings"
            >
              <Clock className="w-4 h-4" />
              {!sideNavCollapsed && <span>Settings</span>}
            </button> */}
            
            {/* Users - COMMENTED OUT FOR NOW */}
            {/* <button 
              className={`nav-item ${location.pathname === "/users" ? "nav-item-active" : ""}`}
              onClick={() => navigate("/users")}
              data-testid="nav-users"
              title="Users"
            >
              <User className="w-4 h-4" />
              {!sideNavCollapsed && <span>Users</span>}
            </button> */}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          <Routes>
            <Route path="/users" element={<UsersPage />} />
            <Route path="/operations/packing" element={<PalletLogDashboard />} />
            <Route path="/dashboard" element={<ProductionDashboard />} />
            <Route path="/items" element={<ItemsPage items={items} onItemsRefresh={fetchItems} />} />
            <Route path="/planning" element={<PlanningDashboard />} />
            <Route path="/planning/raw-materials" element={<PlanningRawMaterialsPage />} />
            <Route path="/planning/finished-goods" element={<PlanningFinishedGoodsPage />} />
            <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="/sales-orders" element={<SalesOrdersPage />} />
            <Route path="/warehouse" element={<WarehousePage />} />
            <Route path="/warehouse/storage-map" element={<StorageMapPage />} />
            <Route path="/warehouse/trucks" element={<TrucksPage />} />
            <Route path="/warehouse/trucks/:truck_id" element={<TruckDetailPage />} />
            <Route path="/warehouse/holds" element={<HoldsPage />} />
            <Route path="/warehouse/transactions" element={<TransactionsPage />} />
            <Route path="/warehouse/receipts" element={<ReceiptsPage />} />
            <Route path="/warehouse/utilization" element={<StorageMapPage />} />
            <Route path="/work-order/:wo_id" element={<WorkOrderDetailPage />} />
            <Route path="/" element={
              <>
      {/* Scheduling Grid - Single Calendar View */}
      <DndContext 
        sensors={sensors} 
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="schedule-container-single" data-testid="schedule-grid">
          <div className="calendar-header">
            <div className="calendar-header-top">
              <div className="calendar-title-section">
                <h3 className="calendar-title">
                  <Factory className="w-5 h-5 inline mr-2" />
                  Production Schedule
                </h3>
              </div>
              
              {/* View Toggle and Week Navigation */}
              <div className="view-controls">
                <div className="view-toggle">
                  <button
                    className={`view-btn ${viewMode === "week" ? "view-btn-active" : ""}`}
                    onClick={() => setViewMode("week")}
                    data-testid="view-week-btn"
                  >
                    Week
                  </button>
                  <button
                    className={`view-btn ${viewMode === "month" ? "view-btn-active" : ""}`}
                    onClick={() => setViewMode("month")}
                    data-testid="view-month-btn"
                  >
                    Month
                  </button>
                  <button
                    className={`view-btn ${viewMode === "max" ? "view-btn-active" : ""}`}
                    onClick={() => setViewMode("max")}
                    data-testid="view-max-btn"
                  >
                    Max
                  </button>
                  <button
                    className={`view-btn ${viewMode === "custom" ? "view-btn-active" : ""}`}
                    onClick={() => setShowCustomDateModal(true)}
                    data-testid="view-custom-btn"
                  >
                    Custom
                  </button>
                </div>
                
                <div className="compact-week-nav">
                  {viewMode === "custom" ? (
                    <span className="compact-week-label">
                      {customStartDate} to {customEndDate}
                    </span>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setCurrentWeek(addWeeks(currentWeek, -1))}
                        className="compact-nav-btn"
                        data-testid="prev-week-btn"
                      >
                        <ChevronLeft className="w-3 h-3" />
                      </Button>
                      <span className="compact-week-label">
                        {format(currentWeek, "MMM d, yyyy")}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
                        className="compact-nav-btn"
                        data-testid="next-week-btn"
                      >
                        <ChevronRight className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Combined Filters: Color Key with Multi-select + Category */}
            <div className="filters-section">
              <div className="filter-box">
                <span className="filter-box-label">Production Lines:</span>
                <div className="line-filters">
                  <label className="line-filter-item">
                    <input
                      type="checkbox"
                      checked={filterLines.includes("P1")}
                      onChange={() => toggleLineFilter("P1")}
                      className="line-checkbox checkbox-p1"
                      data-testid="filter-checkbox-p1"
                    />
                    <span className="line-label">P1</span>
                  </label>
                  <label className="line-filter-item">
                    <input
                      type="checkbox"
                      checked={filterLines.includes("P2")}
                      onChange={() => toggleLineFilter("P2")}
                      className="line-checkbox checkbox-p2"
                      data-testid="filter-checkbox-p2"
                    />
                    <span className="line-label">P2</span>
                  </label>
                  <label className="line-filter-item">
                    <input
                      type="checkbox"
                      checked={filterLines.includes("P3")}
                      onChange={() => toggleLineFilter("P3")}
                      className="line-checkbox checkbox-p3"
                      data-testid="filter-checkbox-p3"
                    />
                    <span className="line-label">P3</span>
                  </label>
                </div>
              </div>
              
              <div className="filter-box">
                <Label className="filter-box-label">Category:</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-[250px] justify-between bg-white"
                    >
                      {selectedCategories.length > 0
                        ? `${selectedCategories.length} selected`
                        : "All Categories"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[250px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search category..." />
                      <CommandList>
                        <CommandEmpty>No category found.</CommandEmpty>
                        <CommandGroup>
                          {availableCategories.map((category) => (
                            <CommandItem
                              key={category}
                              value={category}
                              onSelect={() => {
                                setSelectedCategories((prev) =>
                                  prev.includes(category)
                                    ? prev.filter((c) => c !== category)
                                    : [...prev, category]
                                );
                              }}
                            >
                              <div
                                className={cn(
                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                  selectedCategories.includes(category)
                                    ? "bg-primary text-primary-foreground"
                                    : "opacity-50 [&_svg]:invisible"
                                )}
                              >
                                <Check className={cn("h-4 w-4")} />
                              </div>
                              <span>{category}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                        {selectedCategories.length > 0 && (
                          <>
                            <CommandSeparator />
                            <CommandGroup>
                              <CommandItem
                                onSelect={() => setSelectedCategories([])}
                                className="justify-center text-center"
                              >
                                Clear filters
                              </CommandItem>
                            </CommandGroup>
                          </>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          {/* Clone Mode Indicator */}
          {clonedWorkOrder && (
            <div style={{
              padding: '0.75rem',
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ color: '#92400E', fontWeight: '500' }}>
                📋 Clone mode active: {clonedWorkOrder.item_name} - Click on any day to paste
              </span>
              <button
                onClick={() => {
                  setClonedWorkOrder(null);
                  toast.info("Clone cancelled");
                }}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: '#fff',
                  border: '1px solid #D97706',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  color: '#92400E'
                }}
              >
                Cancel
              </button>
            </div>
          )}
                    
          <div className="days-grid-single">
            {weekDays.map(day => {
              const dayWOs = getWorkOrdersForDate(day);
              return (
                <DroppableDay key={format(day, "yyyy-MM-dd")} date={day}>
                  <div className="day-header">
                    <p className="day-name">{format(day, "EEE")}</p>
                    <p className="day-date">{format(day, "MMM d")}</p>
                  </div>
                  <div className="work-orders-list">
                    {dayWOs.length === 0 ? (
                      <div className="empty-day-clickable">
                        <Plus className="w-4 h-4" />
                        <span>Add work order</span>
                      </div>
                    ) : (
                      dayWOs.map(wo => (
                        <WorkOrderCard
                          key={wo.wo_id}
                          workOrder={wo}
                          onClick={() => handleWorkOrderClick(wo)}
                        />
                      ))
                    )}
                  </div>
                </DroppableDay>
              );
            })}
          </div>
        </div>
        
        <DragOverlay dropAnimation={null}>
          {activeId ? (
            <WorkOrderCard 
              workOrder={workOrders.find(wo => wo.wo_id === activeId)} 
              onClick={() => {}}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Create Work Order Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="modal-content" data-testid="create-work-order-modal" aria-describedby="create-wo-description">
          <DialogHeader>
            <DialogTitle className="modal-title">Create New Work Order</DialogTitle>
            <p id="create-wo-description" className="sr-only">
              Fill in the form to create a new production work order
            </p>
          </DialogHeader>
          <div className="form-grid">
            <div>
              <Label htmlFor="production_line">Production Line *</Label>
              <Select 
                value={newWO.production_line} 
                onValueChange={(value) => setNewWO({...newWO, production_line: value})}
              >
                <SelectTrigger data-testid="production-line-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="P1">P1</SelectItem>
                  <SelectItem value="P2">P2</SelectItem>
                  <SelectItem value="P3">P3</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="customer_po">Customer PO #</Label>
              <Input
                id="customer_po"
                value={newWO.customer_po}
                onChange={(e) => setNewWO({...newWO, customer_po: e.target.value})}
                placeholder="PO-12345"
                data-testid="customer-po-input"
              />
            </div>
            <div>
              <Label htmlFor="item">Item *</Label>
              <Select
                value={newWO.item_id}
                onValueChange={(value) => setNewWO({...newWO, item_id: value})}
              >
                <SelectTrigger data-testid="item-select">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Filter for items that are flagged as assembly AND have BOMs
                    const assemblyItemsWithBOMs = items.filter(item => {
                      const isAssemblyFlagged = item.is_assembly_item === true || item.itemtype === "Assembly" || item.itemtypetext === "Assembly";
                      const hasBOM = item.has_bom === true;
                      console.log(`[DEBUG] Item ${item.displayname || item.name}: is_assembly_item=${item.is_assembly_item}, itemtype=${item.itemtype}, has_bom=${item.has_bOM}`);
                      return isAssemblyFlagged && hasBOM;
                    });
                    
                    console.log(`[DEBUG] Total items: ${items.length}, Assembly items with BOMs: ${assemblyItemsWithBOMs.length}`);
                    
                    if (assemblyItemsWithBOMs.length > 0) {
                      return assemblyItemsWithBOMs.map(item => (
                        <SelectItem key={item.id || item.item_id} value={item.id || item.item_id}>
                          {/* Prioritize itemid to match the Items Page list view */}
                          {item.itemid || item.name || item.displayname}
                        </SelectItem>
                      ));
                    } else {
                      return (
                        <div className="px-2 py-2 text-sm text-gray-500">
                          No assembly items with BOMs found. Try syncing items from NetSuite.
                        </div>
                      );
                    }
                  })()}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-1">
                {(() => {
                  const assemblyItemsWithBOMs = items.filter(item => {
                    const isAssemblyFlagged = item.is_assembly_item === true || item.itemtype === "Assembly" || item.itemtypetext === "Assembly";
                    const hasBOM = item.has_bom === true;
                    return isAssemblyFlagged && hasBOM;
                  });
                  if (assemblyItemsWithBOMs.length > 0) {
                    return `✅ Showing ${assemblyItemsWithBOMs.length} assembly items with BOMs.`;
                  } else {
                    return "❌ No assembly items with BOMs found. Try syncing items from NetSuite.";
                  }
                })()}
              </p>
            </div>
            <div>
              <Label htmlFor="subsidiary">Subsidiary *</Label>
              <Select
                value={newWO.subsidiary_id}
                onValueChange={(value) => {
                  const selected = subsidiaries.find(s => s.id === value);
                  setNewWO({
                    ...newWO, 
                    subsidiary_id: value,
                    subsidiary_name: selected?.name || ""
                  });
                }}
              >
                <SelectTrigger data-testid="subsidiary-select">
                  <SelectValue placeholder="Select subsidiary" />
                </SelectTrigger>
                <SelectContent>
                  {subsidiaries.length > 0 ? (
                    subsidiaries.map(subsidiary => (
                      <SelectItem key={subsidiary.id} value={subsidiary.id}>
                        {subsidiary.name} (ID: {subsidiary.id})
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-2 text-sm text-gray-500">
                      No subsidiaries available
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="location">Location *</Label>
              <Select
                value={newWO.location_id}
                onValueChange={(value) => {
                  const selected = locations.find(l => l.id === value);
                  setNewWO({
                    ...newWO, 
                    location_id: value,
                    location_name: selected?.name || ""
                  });
                }}
              >
                <SelectTrigger data-testid="location-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.length > 0 ? (
                    locations.map(location => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name} (ID: {location.id})
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-2 text-sm text-gray-500">
                      No locations available
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="planned_quantity">Planned Quantity *</Label>
              <Input
                id="planned_quantity"
                type="number"
                value={newWO.planned_quantity}
                onChange={(e) => setNewWO({...newWO, planned_quantity: e.target.value})}
                placeholder="1000"
                data-testid="planned-quantity-input"
              />
            </div>
            <div>
              <Label htmlFor="planned_date">Date *</Label>
              <Input
                id="planned_date"
                type="date"
                value={newWO.planned_date}
                onChange={(e) => setNewWO({...newWO, planned_date: e.target.value})}
                min={format(new Date(), "yyyy-MM-dd")}
                data-testid="planned-date-input"
              />
            </div>
            <div>
              <Label htmlFor="start_time">Start Time *</Label>
              <Input
                id="start_time"
                type="time"
                value={newWO.planned_start_time}
                onChange={(e) => setNewWO({...newWO, planned_start_time: e.target.value})}
                data-testid="start-time-input"
              />
            </div>
            <div>
              <Label htmlFor="end_time">End Time *</Label>
              <Input
                id="end_time"
                type="time"
                value={newWO.planned_end_time}
                onChange={(e) => setNewWO({...newWO, planned_end_time: e.target.value})}
                data-testid="end-time-input"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newWO.notes}
                onChange={(e) => setNewWO({...newWO, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={3}
                data-testid="notes-textarea"
              />
            </div>
          </div>
          <div className="modal-actions">
            <Button variant="outline" onClick={() => setShowCreateModal(false)} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleCreateWorkOrder} data-testid="create-btn">
              Create Work Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Date Range Modal */}
      <Dialog open={showCustomDateModal} onOpenChange={setShowCustomDateModal}>
        <DialogContent className="modal-content max-w-md">
          <DialogHeader>
            <DialogTitle>Select Custom Date Range</DialogTitle>
          </DialogHeader>
          <div className="custom-date-form" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <div>
              <Label htmlFor="custom-start-date">Start Date</Label>
              <Input
                id="custom-start-date"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                data-testid="custom-start-date-input"
              />
            </div>
            <div>
              <Label htmlFor="custom-end-date">End Date</Label>
              <Input
                id="custom-end-date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                data-testid="custom-end-date-input"
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button 
                variant="outline" 
                onClick={() => setShowCustomDateModal(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  setViewMode("custom");
                  setShowCustomDateModal(false);
                }}
                data-testid="apply-custom-dates-btn"
              >
                Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Work Order Detail Modal */}
      {selectedWorkOrder && (
        <WorkOrderDetailModal
          open={showDetailModal}
          onOpenChange={setShowDetailModal}
          workOrder={selectedWorkOrder}
          items={items}
          onUpdate={handleUpdateWorkOrder}
          onDelete={handleDeleteWorkOrder}
          onClone={handleCloneWorkOrder}
        />
      )}
        </>
            } />
            <Route path="/schedule" element={<Navigate to="/" replace />} />
          </Routes>

        </main>
      </div>
    </div>
          </ProtectedRoute>
        } />
      </Routes>
    </>
  );
}

export default App;
